// Single source of truth for the plans, prices, comparison table and pricing FAQs
// shown on the website home page (#pricing) and on /pricing. Both pages used to
// carry their own copy, which is how they drifted apart.
//
// Feature lines marked "all plans" are not gated by plan in the app today
// (see sidebarNavConfig.js), so they are listed as included everywhere.

// The dealership & finance suite is Enterprise-only and sold on request, so it is listed with a
// "Contact us" note rather than a price. Basic Inventory stays on Pro; the advanced procurement and
// stock-control pieces (PO, GRN, multi-warehouse, batch/serial, stock take) are part of this suite.
export const ENTERPRISE_SUITE_FEATURES = [
  { text: 'Dealership Management (DMS)', included: true, highlight: true, note: 'Contact us' },
  { text: 'Finance & accounting', included: true, highlight: true, note: 'Contact us' },
  { text: 'Purchase orders, GRN & advanced stock control', included: true, highlight: true, note: 'Contact us' },
  { text: 'AI Assistant on WhatsApp, 24x7', included: true, highlight: true, note: 'Contact us' },
];

export const PRICING_PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    tagline: 'Best for getting started',
    price: 'Free',
    period: '',
    badge: null,
    cta: 'Get Started Free',
    ctaStyle: 'secondary',
    features: [
      { text: 'Unlimited orders, incl. 5 WhatsApp alerts/month', included: true, whatsapp: true },
      { text: '1 service', included: true },
      { text: '1 crew member', included: true },
      { text: 'Basic invoicing', included: true },
      { text: 'Basic employee management', included: true },
      { text: 'Quotations, bid requests & recurring subscriptions', included: true },
      { text: 'Booking workflows, tasks & resources', included: true },
      { text: 'Analytics Dashboard', included: false },
      { text: 'Stripe integration', included: false },
      { text: 'Inventory management', included: false },
      { text: 'Expense management', included: false },
      { text: 'Full HRMS & Payroll', included: false },
      { text: 'Social media integration', included: false },
      { text: 'Leads & campaigns', included: false },
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    tagline: 'Best for scaling operations & automation',
    price: '$99',
    originalPrice: '$200',
    period: '/mo',
    badge: 'Pro',
    ctaStyle: 'primary',
    features: [
      { text: 'Unlimited orders, incl. 100 WhatsApp alerts/month', included: true, whatsapp: true },
      { text: '5 services', included: true },
      { text: '25 crew members', included: true },
      { text: 'Advanced invoicing', included: true },
      { text: 'Quotations, bid requests & recurring subscriptions', included: true },
      { text: 'Booking workflows, tasks & resources', included: true },
      { text: 'Analytics Dashboard', included: true, highlight: true },
      { text: 'Stripe integration', included: true, highlight: true, note: 'On-demand billing' },
      { text: 'Inventory management', included: true, highlight: true },
      { text: 'Expense management', included: true, highlight: true, note: 'Operating expenses' },
      { text: 'Full HRMS', included: true, highlight: true, note: 'Attendance, leave, roster' },
      { text: 'Priority Support', included: true, highlight: true },
      { text: 'Payroll', included: false, note: 'Available on request' },
      { text: 'Social media integration', included: false },
      { text: 'Leads & campaigns', included: false },
    ],
    footnote: 'Additional alerts beyond 100 are billed per message',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    tagline: 'For scaling operations',
    price: 'Custom',
    period: '',
    badge: null,
    cta: 'Contact Sales',
    ctaStyle: 'dark',
    features: [
      { text: 'Unlimited orders & WhatsApp', included: true, whatsapp: true },
      { text: 'Unlimited services', included: true },
      { text: 'Unlimited crew', included: true },
      { text: 'Full invoicing suite', included: true },
      { text: 'Quotations, bid requests & recurring subscriptions', included: true },
      { text: 'Booking workflows, tasks & resources', included: true },
      { text: 'Analytics Dashboard', included: true },
      { text: 'Stripe integration', included: true },
      { text: 'Inventory management', included: true },
      { text: 'Expense management', included: true, note: 'Operating expenses' },
      { text: 'Full HRMS', included: true, note: 'Attendance, leave, roster' },
      { text: 'Payroll', included: true, highlight: true },
      { text: 'Priority Support', included: true },
      { text: 'Social media integration', included: true, highlight: true },
      { text: 'Leads & campaigns', included: true, highlight: true },
      ...ENTERPRISE_SUITE_FEATURES,
    ],
  },
];

// [feature, Starter, Pro, Enterprise] - "—" renders as not included.
export const COMPARISON_ROWS = [
  ['Monthly orders', 'Unlimited', 'Unlimited', 'Unlimited'],
  ['Services', '1', '5', 'Unlimited'],
  ['Crew members', '1', '25', 'Unlimited'],
  ['SMS messaging', '✓ (5 alerts)', '✓ (100 alerts)', '✓'],
  ['Invoicing', 'Basic', 'Advanced', 'Full suite'],
  ['Employee management', 'Basic', 'Full HRMS', 'Full HRMS'],
  ['HRMS: attendance, leave, roster, documents', '—', '✓', '✓'],
  ['Payroll', '—', 'On request', '✓'],
  ['Inventory management', '—', '✓', '✓'],
  ['Expense management', '—', '✓', '✓'],
  ['Quotations & bid requests', '✓', '✓', '✓'],
  ['Recurring subscriptions', '✓', '✓', '✓'],
  ['Workflows, tasks & resources', '✓', '✓', '✓'],
  ['Analytics Dashboard', '—', '✓', '✓'],
  ['Stripe integration', '—', '✓', '✓'],
  ['Social media', '—', '—', '✓'],
  ['Leads & campaigns', '—', '—', '✓'],
  ['Dealership Management (DMS)', '—', '—', '✓ Contact us'],
  ['Finance & accounting', '—', '—', '✓ Contact us'],
  ['Purchase orders, GRN & advanced stock control', '—', '—', '✓ Contact us'],
  ['AI Assistant on WhatsApp, 24x7', '—', '—', '✓ Contact us'],
  ['Dedicated support', '—', 'Email', '24/7 priority'],
  ['Custom branding', '—', '—', '✓'],
  ['API access', '—', '—', '✓'],
];

export const PRICING_FAQS = [
  {
    q: 'What is in the Dealership & Finance suite, and how is it priced?',
    a: 'Enterprise includes Dealership Management (vehicle sales, workshop, parts, warranty and after-sales), Finance and accounting with UAE VAT, purchase orders with goods receipts, advanced stock control, and the 24x7 WhatsApp AI Assistant. It is set up and priced for your business, so contact us for a quote.',
  },
  {
    q: 'What happens when I exceed my messaging limit?',
    a: "On the Pro plan, additional orders beyond 100/month are billed at a per-message rate. You'll always be notified before any overage charges apply. Starter users can upgrade anytime to unlock more capacity.",
  },
  {
    q: 'Can I switch plans at any time?',
    a: 'Absolutely. Upgrade or downgrade your plan at any point — changes take effect on your next billing cycle. No lock-in contracts, ever.',
  },
  {
    q: 'How does the WhatsApp integration work?',
    a: 'We handle the full WhatsApp Business setup for you. Orders, confirmations, and updates are sent directly to your customers via WhatsApp — no extra apps needed.',
  },
  {
    q: 'What does Stripe integration include?',
    a: 'Pro and Enterprise plans connect directly to Stripe for seamless payment collection, automated invoicing, and real-time revenue tracking. Available on-demand when you need it.',
  },
  {
    q: 'What does Social Media integration mean?',
    a: 'It unites WhatsApp, Instagram, Messenger and other social media business channels into one inbox — saving your team hours of repetitive work and ensuring no conversation is ever lost.',
  },
  {
    q: 'What does the Starter plan include for employees?',
    a: 'Every plan includes basic employee management. Attendance, leave, rosters, documents and the org chart come with full HRMS on Pro and Enterprise.',
  },
  {
    q: 'Are HRMS and Payroll included in Pro?',
    a: 'Pro includes full HRMS — attendance, leave, roster, documents and org chart. Payroll is included in Enterprise and can be added to Pro on request.',
  },
];

// Pro is the only plan with a number on it. Prices are per country code.
const PRO_PRICES = {
  monthly: { AE: 'AED 349', IN: '₹820', US: '$99' },
  annual: { AE: 'AED 300', IN: '₹680', US: '$83' },
};
const PRO_ORIGINAL_PRICE = { AE: 'AED 740', IN: '₹1640', US: '$200' };
const PRO_ANNUAL_BILLED = { AE: 'AED 3,600/yr', IN: '₹8,200/yr', US: '$990/yr' };

const byCountry = (table, country) => table[country] || table.US;

// "Free" / "Custom" pass through; Pro resolves by country and billing period.
export function getPlanPrice(plan, country, annual) {
  if (plan.price === 'Free' || plan.price === 'Custom') return plan.price;
  return byCountry(annual ? PRO_PRICES.annual : PRO_PRICES.monthly, country);
}

export const getOriginalPrice = (country) => byCountry(PRO_ORIGINAL_PRICE, country);
export const getAnnualBilledNote = (country) => byCountry(PRO_ANNUAL_BILLED, country);
